import * as angular from 'angular';
import { ActivitiesService } from 'app/_services/activities.service';
import { LastBidFilter } from 'app/_util/last-bid.filter';
import { Decimal } from 'decimal.js';
import { formatDecimal } from '../../angular/utils/number-helper.util';

export class AuctionsOverviewService {
    /**
     * An info alert text occurring across the page every now and then so it makes sense to make it a constant.
     *
     * @memberof auctionsOverviewService
     * @type {string}
     */
    readonly MINIMUM_BID_INFO_BLAH =
        'Minimum bid: 1 kWh/h, PRISMA uses the English decimal point system. e.g. 1,000.043';

    readonly ACTION_TYPE = {
        SHOW_DETAILS: 'SHOW_DETAILS',
        BID: 'BID',
        NO_PARTICIPATION: 'NO_PARTICIPATION'
    };

    readonly PERIOD_TYPE: {
        [periodType: string]: {
            LABEL: string;
            VALUE: string;
            NORMALIZED: string;
        };
    } = {
        WITHIN_DAY: {
            LABEL: 'WITHIN DAY',
            VALUE: 'WITHINDAY',
            NORMALIZED: 'Within day'
        },
        DAILY: {
            LABEL: 'DAILY',
            VALUE: 'DAY',
            NORMALIZED: 'Day ahead'
        },
        MONTHLY: {
            LABEL: 'MONTHLY',
            VALUE: 'MONTH',
            NORMALIZED: 'Monthly'
        },
        QUARTERLY: {
            LABEL: 'QUARTERLY',
            VALUE: 'QUARTER',
            NORMALIZED: 'Quarterly'
        },
        YEARLY: {
            LABEL: 'YEARLY',
            VALUE: 'YEAR',
            NORMALIZED: 'Yearly'
        },
        REST_OF_YEAR: {
            LABEL: 'REST OF YEAR',
            VALUE: 'REST_OF_YEAR',
            NORMALIZED: 'Rest of year'
        }
    };

    bidDeletedSuccessfully = false;
    comfortBidWithdrawnSuccessfully = false;
    comfortBidUpdatedSuccessfully = false;

    constructor(
        private $filter: ng.IFilterService,
        private $translate: ng.translate.ITranslateService,
        private activitiesService: ActivitiesService,
        private fieldKeyService,
        private log: ng.ILogService
    ) {}

    /**
     * Get to known if the <tt>auctionPeriodType</tt> refers to a short term or long term auction type.
     *
     * @memberof auctionsOverviewService
     * @param {string} auctionPeriodType Should be a key of {@link PERIOD_TYPE}.
     * @returns {boolean} true, if <tt>auctionPeriodType</tt> is a long term auction type,
     *  false if it's a short term auction.
     */
    isLongTermAuction(auctionPeriodType) {
        let shortTermPeriodTypes = [
            this.PERIOD_TYPE.WITHIN_DAY,
            this.PERIOD_TYPE.DAILY
        ];
        return shortTermPeriodTypes.indexOf(auctionPeriodType) === -1;
    }

    firstPeriodTypeToContainAuctions(calendarResponse) {
        for (let periodType in this.PERIOD_TYPE) {
            if (!this.PERIOD_TYPE.hasOwnProperty(periodType)) {
                continue;
            }

            if (
                calendarResponse.hasOwnProperty(
                    this.PERIOD_TYPE[periodType].VALUE
                )
            ) {
                return this.PERIOD_TYPE[periodType];
            }
        }
        return false;
    }

    firstPeriodTypeWithAuctions(response) {
        for (let periodType in this.PERIOD_TYPE) {
            if (!this.PERIOD_TYPE.hasOwnProperty(periodType)) {
                continue;
            }
            for (let i = 0; i <= response.length; i++) {
                if (
                    !angular.isUndefined(response[i]) &&
                    response[i].periodType ===
                        this.PERIOD_TYPE[periodType].VALUE
                ) {
                    return this.PERIOD_TYPE[periodType];
                }
            }
        }
        return false;
    }

    getPeriodTypeByValue(periodTypeValue) {
        for (let periodType in this.PERIOD_TYPE) {
            if (!this.PERIOD_TYPE.hasOwnProperty(periodType)) {
                continue;
            }

            if (this.PERIOD_TYPE[periodType].VALUE === periodTypeValue) {
                return this.PERIOD_TYPE[periodType];
            }
        }

        throw new Error(
            'Getting period type by value, but value "' +
                periodTypeValue +
                '" doesn\'t exist.'
        );
    }

    getPeriodNameByType(periodType): string {
        switch (periodType) {
            case 'WITHINDAY':
                return 'WID';
            case 'DAY':
                return 'DA';
            case 'MONTH':
                return 'Month';
            case 'QUARTER':
                return 'Quarter';
            case 'YEAR':
                return 'Year';
            default:
                return 'Unknown';
        }
    }

    /**
     * From many runtimes get the earliest start and last end as a new runtime
     *
     * @memberof auctionsOverviewService
     * @param {Array} runtimes
     * @returns {Object}
     */
    getTotalRuntimeFromManyRuntimes(runtimes) {
        let orderByFilter: ng.IFilterOrderBy =
            this.$filter<ng.IFilterOrderBy>('orderBy');
        let orderedStartRuntimes: any[] = orderByFilter(runtimes, 'start');
        let orderedEndRuntimes: any[] = orderByFilter(runtimes, '-end');
        return {
            start: orderedStartRuntimes[0].start,
            end: orderedEndRuntimes[0].end
        };
    }

    getAuctionCategoriesToRequest(isLongTerm, phase) {
        if (!isLongTerm) {
            return this.activitiesService.CATEGORY.SHORT_TERM_AUCTION;
        }

        if (
            phase === this.activitiesService.STATE.OPEN ||
            phase === this.activitiesService.STATE.CLOSED
        ) {
            return this.activitiesService.CATEGORY.LONG_TERM_AUCTION;
        } else {
            return this.activitiesService.CATEGORY.COMFORT_BID;
        }
    }

    /**
     * Get the unified bids with the matching id.
     * When there are no unified bids, an object with an empty order curve and `originOrderId` set to `bidId`
     * will be returned. In some cases previously entered bids are not relevant anymore, so they won't be retrieved
     * to be editable. In that case there's no unified bid provided.
     *
     * @memberof auctionsOverviewService
     *
     * @param {LongTermComfortBid[]} longTermUnifiedBids An array of long term bids, each containing an order curve with
     *  unified bids.
     * @param {number} bidId The id of the comfort bid to find in the comfort bids. Will be matched against either
     *  bidId or originOrderId.
     * @param {boolean} cleanUpForPost `true` if the resulting JSON should be cleaned up for a backend-compatible
     *  JSON. Elements `id`, `createdAt`, `createdBy`, `state` and `auctionId` would be deleted from the JSON.
     *
     * @returns {Object} The comfort bid with the matching `originOrderId`.
     *
     * @see {@link app.LongTermComfortBid} An array of this object is passed.
     * @see {@link app.LongTermComfortBid#orderCurve} This contains the unified bids.
     */
    findUnifiedBidsById(longTermUnifiedBids, bidId, cleanUpForPost) {
        for (let i = 0; i < longTermUnifiedBids.length; i++) {
            if (
                longTermUnifiedBids[i].originOrderId == bidId ||
                longTermUnifiedBids[i].id == bidId
            ) {
                let longTermUnifiedBid = longTermUnifiedBids[i];

                if (cleanUpForPost) {
                    longTermUnifiedBids[i].originOrderId =
                        longTermUnifiedBids[i].id;
                    delete longTermUnifiedBid.id;
                    delete longTermUnifiedBid.createdAt;
                    delete longTermUnifiedBid.createdBy;
                    delete longTermUnifiedBid.state;
                    delete longTermUnifiedBid.auctionId;
                }

                return longTermUnifiedBid;
            }
        }

        return {
            originOrderId: bidId,
            orderCurve: []
        };
    }

    /**
     * Check if any fees (also known as charges) are available.
     *
     * @memberof auctionsOverviewService
     * @param feesObj The object containing the fees.
     * @param {string|undefined} tsoNumber Used to get the fee like biogasCharge0 or biogasCharge1.
     *  If not specified, it will use recursion to check for bundle case 0 and 1.
     * @returns {boolean}
     */
    hasFeesAttached(feesObj, tsoNumber) {
        let possibleCharges = [
            'measurementCharge',
            'biogasCharge',
            'operatingCharge',
            'accountingCharge',
            'gasQualityConversionCharge',
            'regulatedTariff',
            'balancingCharge',
            'commodityFee'
        ];

        if (angular.isUndefined(tsoNumber)) {
            if (this.hasFeesAttached(feesObj, '1')) {
                return true;
            }
            tsoNumber = '0';
        }

        for (let i = 0; i < possibleCharges.length; i++) {
            let obj = possibleCharges[i];

            if (feesObj[obj + tsoNumber]) {
                return true;
            }
        }

        return false;
    }

    hasPastBids(windows) {
        if (windows.length < 1) {
            return false;
        }
        return !(windows.length < 2 && windows[0].activeBid);
    }

    /**
     * Get the active bid from an array of unified bids.
     *
     * @memberof auctionsOverviewService
     * @param {app.OrderCurvePointDto[]} unifiedBids
     * @returns {app.OrderCurvePointDto|null} <tt>null<tt> when there's no active bid in the curve.
     */
    getActiveBidFromUnifiedBids(unifiedBids) {
        for (let i = 0; i < unifiedBids.length; i++) {
            if (unifiedBids[i].active === true && unifiedBids[i].quantity) {
                return unifiedBids[i];
            }
        }
        return null;
    }

    /**
     * This method determines the min and max quantities which will be passed to the pkpAuctionDetails directive
     * for further calculation regarding volume-based regulated tariffs.
     *
     * @memberof auctionsOverviewService
     * @param {Object[]} orderCurve
     * @returns {{minQuantity: Object|null, maxQuantity: Object|null}} Null values for an empty order curve.
     */
    findMinAndMaxQuantities(orderCurve) {
        if (!orderCurve.length) {
            return {
                minQuantity: null,
                maxQuantity: null
            };
        }

        let minQuantity = orderCurve[0].quantity;
        let maxQuantity = orderCurve[0].quantity;

        for (let i = 0; i < orderCurve.length; i++) {
            let curvePoint = orderCurve[i];

            try {
                let quantityAsDecimal = new Decimal(
                    curvePoint.quantity.originalValue.value
                );

                if (
                    quantityAsDecimal.greaterThan(
                        maxQuantity.originalValue.value
                    )
                ) {
                    maxQuantity = curvePoint.quantity;
                }
                if (
                    quantityAsDecimal.lessThan(minQuantity.originalValue.value)
                ) {
                    minQuantity = curvePoint.quantity;
                }
            } catch (e) {}
        }

        return {
            minQuantity: minQuantity,
            maxQuantity: maxQuantity
        };
    }

    /**
     * @memberof app
     */
    isValidCurvePoint(price, quantity) {
        if (!price || !quantity) {
            return false;
        }

        return (
            !isNaN(parseFloat(price)) &&
            isFinite(price) &&
            !isNaN(parseFloat(quantity)) &&
            isFinite(quantity)
        );
    }

    /**
     * Remove all curve points that have an empty surcharge or amount.
     *
     * @memberof auctionsOverviewService
     * @param {Object[]} orderCurve The unfiltered cluttered order curve.
     * @param {string} valueKey The value accessor key which can be either `value` or `originalValue`.
     * @returns {Object[]} The filtered order curve without points with an empty surcharge or amount.
     */
    filterInvalidCurvePoint(orderCurve, valueKey) {
        let filteredOrderCurve = [];
        for (let i = 0; i < orderCurve.length; i++) {
            let curvePoint = orderCurve[i];

            let validCurvePoint;
            try {
                validCurvePoint = this.isValidCurvePoint(
                    curvePoint.price[valueKey].value,
                    curvePoint.quantity[valueKey].value
                );
            } catch (e) {
                continue;
            }

            if (validCurvePoint) {
                filteredOrderCurve.push(curvePoint);
            }
        }

        return filteredOrderCurve;
    }

    /**
     * Get the end time of the current phase which is the start time of the next phase.
     *
     * @memberof auctionsOverviewService
     * @param {PHASE} currentPhase
     * @param {Object<string, string>} triggerTime
     * @param {string} triggerTime.auctionStart Applies on published.
     * @param {string} triggerTime.nextClose Applies on open.
     * @param {string} triggerTime.nextOpen Applies on closed.
     * @returns {?string} The date and time left to the end of the current phase and until the next.
     *  <tt>null</tt> when the phase is none of published, open or closed.
     */
    startOfNextPhase(currentPhase, triggerTime) {
        let PHASE = this.activitiesService.STATE;

        if (currentPhase === PHASE.PUBLISHED) {
            return triggerTime.auctionStart;
        } else if (currentPhase === PHASE.OPEN) {
            return triggerTime.nextClose;
        } else if (currentPhase === PHASE.CLOSED) {
            return triggerTime.nextOpen;
        }

        return null;
    }

    /**
     * From a list of prices you want to get the one that matches the unit you want.
     * If that's what you want, here is your function.
     *
     * @memberof auctionsOverviewService
     * @param {app.PriceDto[]} prices The prices with one of them matching the <tt>targetUnit</tt>.
     * @param {app.PriceUnitDto} targetUnit The target unit that needs to be found in <tt>prices</tt>.
     * @param {boolean?} [useOriginals=false]
     * @returns {app.PriceDto} The price with the matching unit.
     */
    extractPriceWithMatchingUnit(prices, targetUnit, useOriginals) {
        let fieldKey = this.fieldKeyService.initialize(useOriginals !== true);

        for (let i = 0; i < prices.length; i++) {
            let price = prices[i];

            if (angular.equals(price[fieldKey.unitKey], targetUnit)) {
                return price;
            }
        }

        this.log.error(
            "Unit mismatch: Couldn't find target unit " +
                JSON.stringify(targetUnit) +
                ' in prices ' +
                JSON.stringify(prices)
        );
        return prices[0];
    }

    /**
     * Calculate the maximum quantity that can be ordered based on the different scenarios that are possible for
     * long term auctions.
     *
     * @memberof auctionsOverviewService
     * @param {app.QuantityDto} marketable
     * @param {app.QuantityDto} [maximumOrderQuantity]
     * @param {string} round
     * @param {app.AuctionWindowDto[]} windows
     * @param {PkpUseOriginals} [useOriginals]
     * @returns {{quantity: number, message: string, unit: string}}
     *  <tt>quantity</tt>: The maximal quantity that can be ordered in the current scenario.
     *  <tt>message</tt>: The message that can be displayed as the validation failure message.
     *  Be aware that this contains HTML so bind to <tt>ngBindHtml</tt>.
     *  <tt>unit</tt>: The unit of the quantity.
     */
    calcMaxLongTermBid(
        marketable,
        maximumOrderQuantity,
        round,
        windows,
        useOriginals
    ) {
        let fieldKey = this.fieldKeyService.initialize(useOriginals);
        let lastBidFilter: LastBidFilter =
            this.$filter<LastBidFilter>('pkpLastBid');
        let lastActiveBid = lastBidFilter(windows, round);
        let defaultMaxBid = {
            quantity: marketable[fieldKey.valueKey].value.toNumber(),
            message:
                'Amount exceeds marketable capacity of ' +
                formatDecimal(marketable[fieldKey.valueKey].value) +
                '&nbsp;' +
                marketable[fieldKey.unitKey] +
                '.',
            unit: marketable[fieldKey.unitKey]
        };

        if (round === '0.0' || (!maximumOrderQuantity && !lastActiveBid)) {
            return defaultMaxBid;
        }

        if (
            maximumOrderQuantity &&
            (!lastActiveBid ||
                maximumOrderQuantity[fieldKey.valueKey].value.lessThan(
                    lastActiveBid[fieldKey.valueKey].value
                ))
        ) {
            return {
                quantity:
                    maximumOrderQuantity[fieldKey.valueKey].value.toNumber(),
                message:
                    'Due to bid limitation in this auction, the maximum bid amount is limited to ' +
                    formatDecimal(
                        maximumOrderQuantity[fieldKey.valueKey].value
                    ) +
                    '&nbsp;' +
                    maximumOrderQuantity[fieldKey.unitKey] +
                    '.',
                unit: maximumOrderQuantity[fieldKey.unitKey]
            };
        }

        return {
            quantity: lastActiveBid[fieldKey.valueKey].value.toNumber(),
            message:
                'Amount exceeds your previous bid of ' +
                formatDecimal(lastActiveBid[fieldKey.valueKey].value) +
                '&nbsp;' +
                lastActiveBid[fieldKey.unitKey] +
                '.',
            unit: lastActiveBid[fieldKey.unitKey]
        };
    }

    orderCurveBySurcharge(orderCurve) {
        let orderCurveWithValidSurcharges = this.$filter('filter')(
            orderCurve,
            function (curvePoint: OrderCurvePointDto) {
                try {
                    return !!(
                        curvePoint.quantity.originalValue.value &&
                        (<PriceDto>curvePoint.price).originalValue.value
                    );
                } catch (e) {
                    return false;
                }
            }
        );

        return this.$filter('orderBy')(
            orderCurveWithValidSurcharges,
            function (curvePoint: OrderCurvePointDto) {
                try {
                    return new Decimal(
                        (<PriceDto>curvePoint.price).originalValue.value
                    ).toNumber();
                } catch (e) {
                    return 0;
                }
            }
        );
    }

    /**
     * Deletion of a long term active bid is allowed when the auction is not closed and in a small price step
     * at the same time.
     *
     * @memberof auctionsOverviewService
     * @param {string} round
     * @param {"CLOSED"|string} phase
     * @returns {boolean} `true` if deletion is permitted, `false` otherwise.
     */
    permitLongTermBidDeletion(round, phase) {
        return !(
            phase === 'CLOSED' &&
            this.activitiesService.getSmallAndLargePriceSteps(round)
                .smallPriceSteps > 0
        );
    }
}

/**
 * @ngdoc service
 * @name auctionsOverviewService
 * @memberof app
 *
 * @description
 * Provides lots of helper functions and logic regarding auctions.
 *
 * @author flzemke.btc
 */
angular
    .module('app')
    .service('auctionsOverviewService', AuctionsOverviewService);
